import axios from 'axios';
import useProjectsApi from '../../projects/projectsApi';
import { ValidationError } from '../http/requestSender';

export enum FileType {
  PROJECT_DOCUMENT = 'PROJECT_DOCUMENT',
}

export interface FileDto {
  id: number,
  name: string,
  mimeType: string,
}

export interface DownloadFileDto {
  downloadUrl: string,
  fileName: string
  size: number
  mimeType: string
}

export interface CreateUploadLinkResponse {
  uploadLink: string,
  file: FileDto,
}

export function useMimeTypesHelper() {

  const isPdfMimeType = (mimeType: string) => {
    return mimeType === 'application/pdf';
  };

  const isWordMimeType = (mimeType: string) => {
    return [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
    ].includes(mimeType);
  };

  return {
    isPdfMimeType,
    isWordMimeType,
  };
}

function useFileValidator() {

  const toMb = (size: number) => {
    return size / 1024 / 1024;
  };

  const fromMb = (size: number) => {
    return size * 1024 * 1024;
  };

  const validateFileSize = (file: File, maxSize: number) => {
    if (file.size > maxSize) {
      return [
        `File size exceeds limit of ${toMb(maxSize)} Mb`,
      ];
    } else {
      return [];
    }
  };

  const validateMimeType = (
    file: File,
    allowedMimeTypes: string[],
    errorMessage?: string,
  ) => {
    if (!allowedMimeTypes.includes(file.type)) {
      if (errorMessage) {
        return [errorMessage];
      } else {
        return [
          `File type ${file.type} is not allowed. Allowed files: ${allowedMimeTypes.join(', ')}`,
        ];
      }
    } else {
      return [];
    }
  };

  const validateFile = async (file: File, fileType: FileType) => {
    if (fileType === FileType.PROJECT_DOCUMENT) {
      return [
        ...validateFileSize(file, fromMb(25)),
        ...validateMimeType(
          file,
          [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.oasis.opendocument.text',
            'application/rtf',
          ],
          'Only PDF and Microsoft Word files are allowed.',
        ),
      ];
    } else {
      return [];
    }
  };

  return {
    validateFile,
    validateMimeType,
  };
}

export default function useFileService() {
  const nekstApi = useProjectsApi();

  const fileValidator = useFileValidator();

  const createFileUploadLink = async (
    fileType: FileType,
    fileName: string,
  ) => {
    return await nekstApi.post('/files/uploadlinks', {
      fileType,
      fileName,
    }) as CreateUploadLinkResponse;
  };

  async function getFileData(file: File): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const fileData = reader.result as ArrayBuffer;
        resolve(fileData);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  function getFileContentType(file: File): string {
    return file.type || 'application/octet-stream';
  }

  const doUploadFile = async (
    file: File,
    fileName: string,
    uploadLink: string,
    onProgress?: (value: number) => void,
  ) => {
    const fileData = await getFileData(file);

    const onUploadProgress = (progressEvent: ProgressEvent) => {
      const {
        loaded,
        total,
      } = progressEvent;
      const percent = Math.floor((loaded * 100) / total);
      if (onProgress) {
        onProgress(percent);
      }
    };

    await axios.put(
      uploadLink,
      fileData,
      {
        headers: {
          'Content-Type': getFileContentType(file),
          'Content-Disposition': `attachment; filename="${fileName}"`,
        },
        onUploadProgress,
      },
    );
  };

  const uploadFile = async (
    file: File,
    fileType: FileType,
    fileName: string,
    onUploadProgress?: (value: number) => void,
  ): Promise<FileDto> => {
    const validationErrors = await fileValidator.validateFile(file, fileType);

    if (validationErrors.length === 0) {
      const uploadLinkResponse = await createFileUploadLink(fileType, fileName);

      await doUploadFile(
        file,
        fileName,
        uploadLinkResponse.uploadLink,
        onUploadProgress,
      );

      return uploadLinkResponse.file;
    } else {
      throw new ValidationError({
        fileId: validationErrors,
      });
    }
  };

  return {
    createFileUploadLink,
    uploadFile,
  };
}
